import { SPECIAL_TARGET_LABELS, type MappingTarget } from '@fundline/shared/imports/mapping';
import { formatCentsAmount } from '@fundline/shared/money';
import {
  type ImportCheck,
  IMPORT_OUTCOME_LABELS,
  IMPORT_OUTCOMES,
  type ImportOutcome,
  type PersonConsentSource,
  type StartImportRequest,
} from '@fundline/shared/schemas/imports';
import type { FieldDefinition } from '../settings/use-fields.ts';

/** The import setup form (M8): what the admin picks, turned into the worker's request. */

export interface ImportFormState {
  /** Column index → target ('' = not imported). */
  mapping: Record<string, MappingTarget | ''>;
  sourceId: string | null;
  saveMappingAs: string;
  assignmentMode: 'single' | 'round_robin';
  assignee: string;
  roundRobin: string[];
  tagIds: string[];
  batchTag: boolean;
  existingMode: 'skip' | 'fill_blanks';
  lookup: boolean;
  consentSource: PersonConsentSource | '';
  consentDetail: string;
  consentScope: string;
  consentOnOrBefore: string;
  attested: boolean;
}

export interface TargetOption {
  value: MappingTarget | '';
  label: string;
}

/** Where a column can go: nowhere, the phone, a field (not removed), consent evidence or the DNC flag. */
export function targetOptions(fields: readonly FieldDefinition[]): TargetOption[] {
  const live = fields.filter((f) => !f.archivedAt && f.systemKey !== 'phone');
  return [
    { value: '', label: "Don't import" },
    { value: 'phone', label: 'Phone' },
    ...live.map((f) => ({ value: `field:${f.key}` as MappingTarget, label: f.label })),
    { value: 'consent_at', label: SPECIAL_TARGET_LABELS.consent_at },
    { value: 'consent_ip', label: SPECIAL_TARGET_LABELS.consent_ip },
    { value: 'consent_url', label: SPECIAL_TARGET_LABELS.consent_url },
    { value: 'consent_text', label: SPECIAL_TARGET_LABELS.consent_text },
    { value: 'vendor_dnc', label: SPECIAL_TARGET_LABELS.vendor_dnc },
  ];
}

/** Problems that stop a check (the worker checks again). */
export function mappingProblem(mapping: ImportFormState['mapping']): string | null {
  const targets = Object.values(mapping).filter((t): t is MappingTarget => t !== '');
  if (!targets.includes('phone')) return 'Choose the column with the phone number.';
  const seen = new Set<string>();
  for (const t of targets) {
    if (seen.has(t)) return 'Two columns go to the same place. Choose one.';
    seen.add(t);
  }
  return null;
}

export function settingsRequest(s: ImportFormState): Omit<StartImportRequest, 'consent'> {
  const mapping: Record<string, MappingTarget> = {};
  for (const [i, t] of Object.entries(s.mapping)) if (t) mapping[i] = t;
  return {
    mapping,
    sourceId: s.sourceId,
    saveMappingAs: s.saveMappingAs.trim() || null,
    assignment:
      s.assignmentMode === 'single'
        ? { mode: 'single', userId: s.assignee }
        : { mode: 'round_robin', userIds: s.roundRobin },
    defaultTagIds: s.tagIds,
    batchTag: s.batchTag,
    existingMode: s.existingMode,
    lookup: { enabled: s.lookup, maxCents: null },
  };
}

/** A key of everything the check depends on: when it changes, the file must be checked again. */
export function checkKey(s: ImportFormState): string {
  return JSON.stringify([settingsRequest(s), s.lookup]);
}

export interface ConsentProblems {
  consentSource?: string;
  consentScope?: string;
  consentOnOrBefore?: string;
  attested?: string;
}

export function consentProblems(s: ImportFormState, today: string): ConsentProblems {
  const p: ConsentProblems = {};
  if (!s.consentSource) p.consentSource = 'Choose where consent came from';
  if (!s.consentScope.trim()) p.consentScope = 'Say what the consent covers';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s.consentOnOrBefore)) p.consentOnOrBefore = 'Choose the date';
  else if (s.consentOnOrBefore > today) p.consentOnOrBefore = 'The date cannot be in the future';
  if (!s.attested) p.attested = 'Confirm this before importing';
  return p;
}

export function startRequest(s: ImportFormState, check: ImportCheck): StartImportRequest | null {
  if (!s.consentSource || !s.attested) return null;
  const base = settingsRequest(s);
  return {
    ...base,
    lookup: { enabled: s.lookup, maxCents: s.lookup ? check.lookupCostCents : null },
    consent: {
      source: s.consentSource,
      sourceDetail: s.consentDetail.trim(),
      scope: s.consentScope.trim(),
      onOrBefore: s.consentOnOrBefore,
      attested: true,
    },
  };
}

export interface CountLine {
  outcome: ImportOutcome;
  label: string;
  count: number;
}

/** The non-zero outcomes, new and filled-in merchants first. */
export function countLines(counts: Record<ImportOutcome, number>): CountLine[] {
  return IMPORT_OUTCOMES.filter((o) => counts[o] > 0).map((o) => ({
    outcome: o,
    label: IMPORT_OUTCOME_LABELS[o],
    count: counts[o],
  }));
}

/** The batch tag's name, as the worker makes it (UTC date, at most 40 characters). */
export function batchTagLabel(fileName: string, now: Date): string {
  const base = fileName.replace(/\.(csv|tsv|txt)$/i, '');
  return `Import ${now.toISOString().slice(0, 10)} ${base}`.slice(0, 40).trim();
}

export function lookupCostText(cents: string, count: number): string {
  if (count === 0) return 'No new numbers to look up.';
  return `${count.toLocaleString('en-US')} new ${count === 1 ? 'number' : 'numbers'}, about ${formatCentsAmount(cents)}.`;
}
